<script setup lang="ts">
import { computed, ref, provide, onErrorCaptured, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores'
import { AppHeader, AppFooter, GuestHeader } from '@/components/layout'
import router from '@/router'

const route = useRoute()
const authStore = useAuthStore()

// Sidebar state - shared with header
const sidebarOpen = ref(false)
provide('sidebarOpen', sidebarOpen)

// Error handling
const appError = ref<string | null>(null)

onErrorCaptured((err) => {
  console.error('App error:', err)
  appError.value = err.toString()
  return false
})

// Global 401 handler — fired by the axios interceptor in api.ts
function handleUnauthenticated(event: Event) {
  if (!authStore.isAuthenticated) return
  const detail = (event as CustomEvent<{ redirect?: string }>).detail
  const redirectPath = detail?.redirect && detail.redirect !== '/login' ? detail.redirect : undefined
  void authStore.logout().then(() => {
    if (redirectPath) {
      void router.replace({ name: 'login', query: { redirect: redirectPath } })
    }
  })
}

onMounted(() => {
  window.addEventListener('auth:unauthenticated', handleUnauthenticated)
})

onUnmounted(() => {
  window.removeEventListener('auth:unauthenticated', handleUnauthenticated)
})

// Determine if we should show header/footer based on route
const showLayout = computed(() => {
  const guestOnlyRoutes = ['login', 'signup', 'forgot-password', 'not-found']
  return authStore.isAuthenticated && !guestOnlyRoutes.includes(route.name as string)
})

const isGuestRoute = computed(() => {
  const guestOnlyRoutes = ['login', 'signup', 'forgot-password']
  return guestOnlyRoutes.includes(route.name as string)
})
</script>

<template>
  <div id="root">
    <div class="App">
      <!-- Error Display -->
      <div v-if="appError" style="padding: 2rem; background: #fee; color: #c00;">
        <h2>Application Error</h2>
        <p>{{ appError }}</p>
        <p>Please check the browser console for more details.</p>
      </div>
      
      <!-- Guest Header (for unauthenticated pages) -->
      <GuestHeader v-if="isGuestRoute" />
      
      <!-- Header with Sidebar (only for authenticated users) -->
      <AppHeader v-if="showLayout" />
      
      <!-- Main Content -->
      <main>
        <RouterView v-slot="{ Component }">
          <component :is="Component" v-if="Component" />
        </RouterView>
      </main>
      
      <!-- Footer (only for authenticated users) -->
      <AppFooter v-if="showLayout" />
    </div>
  </div>
</template>

<style>
@import url("./App.css");
</style>
